import React, { useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward, Search } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const fieldSx = {
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    height: 54,
    bgcolor: '#fff',
    borderRadius: 2,
    '& fieldset': { borderColor: '#d7dee8' },
    '&:hover fieldset': { borderColor: '#91a0b5' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a8a' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a8a' },
};

export default function HeroSlider() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ listingType: 'rent', location: '', beds: '' });

  const handleChange = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const params = new URLSearchParams();

    if (form.listingType) params.set('types', form.listingType);
    if (form.location.trim()) params.set('q', form.location.trim());
    if (form.beds) params.set('beds', form.beds);

    navigate(`/properties?${params.toString()}`);
  };

  return (
    <Box
      component="section"
      aria-labelledby="home-hero-title"
      sx={{
        position: 'relative',
        minHeight: { xs: 700, sm: 660, md: 720 },
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        color: 'common.white',
        bgcolor: '#0b172a',
        backgroundImage: 'url(/media/hero-property-poster.jpg)',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/media/hero-property-poster.jpg"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          '@media (prefers-reduced-motion: reduce)': { display: 'none' },
        }}
      >
        <source src="/media/hero-property.mp4" type="video/mp4" />
      </Box>

      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(6,16,32,.91) 0%, rgba(6,16,32,.78) 48%, rgba(6,16,32,.34) 100%), linear-gradient(0deg, rgba(6,16,32,.45) 0%, transparent 45%)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: 2.5, sm: 4, lg: 6 },
          py: { xs: 7, md: 9 },
        }}
      >
        <Box sx={{ maxWidth: 790 }}>
          <Typography
            component="p"
            sx={{
              mb: 2,
              color: 'rgba(255,255,255,.78)',
              fontSize: { xs: '0.76rem', sm: '0.82rem' },
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            Middlesbrough estate agents &amp; property management
          </Typography>

          <Typography
            id="home-hero-title"
            component="h1"
            sx={{
              maxWidth: 720,
              m: 0,
              fontSize: { xs: '2.75rem', sm: '3.8rem', md: '4.65rem' },
              fontWeight: 800,
              letterSpacing: '-0.045em',
              lineHeight: 0.98,
              textWrap: 'balance',
            }}
          >
            Property, handled properly.
          </Typography>

          <Typography
            sx={{
              mt: 2.5,
              maxWidth: 630,
              color: 'rgba(255,255,255,.84)',
              fontSize: { xs: '1.05rem', sm: '1.18rem' },
              lineHeight: 1.65,
            }}
          >
            Clear, local advice for selling, letting and managing property across Middlesbrough
            and Teesside.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3.5 }}>
            <Button
              component={RouterLink}
              to="/properties"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{ px: 3, py: 1.35, bgcolor: '#fff', color: '#10234b', '&:hover': { bgcolor: '#eef2f7' } }}
            >
              Find a property
            </Button>
            <Button
              component={RouterLink}
              to="/sellers/valuation"
              variant="outlined"
              size="large"
              sx={{
                px: 3,
                py: 1.35,
                color: '#fff',
                borderColor: 'rgba(255,255,255,.62)',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,.08)' },
              }}
            >
              Book a valuation
            </Button>
          </Stack>
        </Box>

        <Paper
          component="form"
          onSubmit={handleSearch}
          elevation={0}
          sx={{
            mt: { xs: 5, md: 6 },
            maxWidth: 1060,
            p: { xs: 1.5, sm: 2 },
            border: '1px solid rgba(255,255,255,.55)',
            borderRadius: 3,
            bgcolor: 'rgba(250,252,255,.96)',
            boxShadow: '0 22px 55px rgba(0,0,0,.24)',
            backdropFilter: 'blur(14px)',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(150px,.72fr) minmax(220px,1.35fr) minmax(130px,.64fr)',
              md: 'minmax(150px,.72fr) minmax(280px,1.45fr) minmax(140px,.64fr) auto',
            },
            gap: 1.25,
          }}
        >
          <TextField
            select
            label="Looking for"
            value={form.listingType}
            onChange={handleChange('listingType')}
            sx={fieldSx}
          >
            <MenuItem value="rent">To rent</MenuItem>
            <MenuItem value="sale">For sale</MenuItem>
            <MenuItem value="auction">At auction</MenuItem>
          </TextField>

          <TextField
            label="Location or postcode"
            placeholder="Middlesbrough or TS1"
            value={form.location}
            onChange={handleChange('location')}
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            label="Bedrooms"
            value={form.beds}
            onChange={handleChange('beds')}
            sx={fieldSx}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="1">1+</MenuItem>
            <MenuItem value="2">2+</MenuItem>
            <MenuItem value="3">3+</MenuItem>
            <MenuItem value="4">4+</MenuItem>
            <MenuItem value="5">5+</MenuItem>
          </TextField>

          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<Search />}
            sx={{
              minHeight: 54,
              px: 3,
              gridColumn: { xs: 'auto', sm: '1 / -1', md: 'auto' },
              bgcolor: '#1e3a8a',
              '&:hover': { bgcolor: '#172e70' },
            }}
          >
            Search
          </Button>
        </Paper>
      </Box>
    </Box>
  );
}
